import { LitElement, PropertyValues, css, html, nothing } from 'lit';
import { customElement, query } from 'lit/decorators.js';
import {
   AtlExpandingSearchProps,
   ExpandingSearchIconVariant,
} from './expanding-search.props.ts';
import classNames from 'classnames';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { enumAttributeConverter } from '../enum-types.ts';
import { getGlobalStyleSheets } from '../../global-styles';
import { AtlSelectOption } from '../forms/select/index.ts';
import {
   SpinnerSize,
   SpinnerAlignment,
   SpinnerVariant,
} from '../spinner/index.ts';
import accessibilityHelper from '../../accessibilityHelper';

import * as TagNames from './expanding-search.tagnames.ts';
import {
   AtlExpandingSearchChangeEvent,
   AtlExpandingSearchKeyDownEvent,
} from './expanding-search.events.ts';
import { throttle } from '../../throttle.ts';

import { BootstrapDropdown } from '../BootstrapDrowndown.ts';

const IconVariantValues = {
   [ExpandingSearchIconVariant.Default]: 'atl-wc-default',
   [ExpandingSearchIconVariant.Cta]: 'atl-wc-primary',
   [ExpandingSearchIconVariant.Nav]: 'atl-wc-nav',
   [ExpandingSearchIconVariant.Secondary]: 'atl-wc-secondary',
   [ExpandingSearchIconVariant.Light]: 'atl-wc-light',
   [ExpandingSearchIconVariant.Destructive]: 'atl-wc-danger',
};

@customElement(TagNames.ExpandingSearch)
export class AtlExpandingSearch
   extends LitElement
   implements AtlExpandingSearchProps
{
   declare listClassName?: string;
   declare displayResults?: boolean;
   declare variant?: ExpandingSearchIconVariant;
   declare options?: Array<AtlSelectOption> | undefined;
   declare protected elementInFocus: boolean;
   declare protected toggled: boolean;
   declare protected loading: boolean;

   private _bootStrapDropdown?: BootstrapDropdown;

   static properties = {
      listClassName: { type: String },
      displayResults: { type: Boolean },
      variant: {
         type: String,
         converter: enumAttributeConverter(
            ExpandingSearchIconVariant,
            ExpandingSearchIconVariant.Cta
         ),
      },
      options: { type: Array, state: true },
      elementInFocus: { type: Boolean, state: true },
      toggled: { type: Boolean, state: true },
      loading: { type: Boolean, state: true },
   };

   constructor() {
      super();
      this.options = [];
      this.addEventListener('blur', this.handleClear);
   }

   @query('.atl-wc-expanding-search-input')
   private searchInput?: HTMLInputElement;

   static styles = [
      ...getGlobalStyleSheets(),
      css`
         .dropdown-item:hover {
            cursor: pointer;
            background-color: #d9d9d9 !important;
            border-radius: 15px;
         }
      `,
   ];

   protected override firstUpdated(changedProperties: PropertyValues): void {
      super.firstUpdated(changedProperties);

      this._bootStrapDropdown = bootstrap.Dropdown.getOrCreateInstance(
         this.searchInput
      );
   }

   protected override updated(changedProperties: PropertyValues): void {
      super.updated(changedProperties);
      if (
         changedProperties.has('displayResults') ||
         changedProperties.has('toggled')
      ) {
         if (this.displayResults && this.toggled) {
            this._bootStrapDropdown?.show();
         } else {
            this._bootStrapDropdown?.hide();
         }
      }
   }

   renderIcon(option: AtlSelectOption) {
      if (!option.icon) return nothing;

      return html`
         <i class=${`fa-solid fa-${option.icon} ${option.iconClassName}`}></i>
      `;
   }

   renderOption(option: AtlSelectOption) {
      return html`
         <li
            class="dropdown-item p-2"
            data-type="user-profile-menu-item"
            @click=${() => this._handleSelection(option.value)}
            @keydown=${(e: KeyboardEvent) =>
               accessibilityHelper.onKeyDownWrapper(e, () =>
                  this._handleItemKeyDown(e, option.value)
               )}
            style="word-wrap: break-word; white-space: normal; word-break: break-word;"
            tabindex="0"
         >
            ${this.renderIcon(option)} ${html`${unsafeHTML(option.label)}`}
         </li>
      `;
   }

   renderOptionsToShow() {
      if (this.options) {
         this.loading = false;
         if (this.options.length > 0) {
            return html`${this.options.map((o) => this.renderOption(o))}`;
         }
      }
      if (this.loading) {
         return html`
            <atlantis-inline-loader
               size=${SpinnerSize.small}
               align=${SpinnerAlignment.TopCenter}
               color=${SpinnerVariant.Primary}
            >
            </atlantis-inline-loader>
         `;
      }

      return html`
         <li class="dropdown-item p-2" data-type="user-profile-menu-item">
            <slot></slot>
         </li>
      `;
   }

   render() {
      const iconClass = [
         'fa-solid fa-search atl-wc-icon',
         IconVariantValues[this.variant ?? ExpandingSearchIconVariant.Default],
      ];
      const containerClass = [
         'atl-wc-expanding-search',
         'dropdown',
         IconVariantValues[this.variant ?? ExpandingSearchIconVariant.Default],
      ];

      return html`
            <div class=${classNames(containerClass)}>         
                <div style="margin-top: -1px; margin-right: -1px;">
                    <i 
                        .aria-hidden="${this.ariaHidden}" 
                        class=${classNames(iconClass)} 
                        part="icon"
                        @click=${this._handleSearchShow}
                    ></i>
                </div>
                <input 
                    class="atl-wc-expanding-search-input"
                    @keydown=${this._throttledChangeEvent}
                >
                </input>                
                <ul class=${classNames('dropdown-menu py-0 border border-secondary rounded', this.listClassName)}                  
                  style="width: inherit;"
                  data-type="user-profile-menu-dropdown"
                >
                  ${this.renderOptionsToShow()}
                </ul>
            </div>
        `;
   }

   protected _handleSearchShow = () => {
      this.elementInFocus = !this.elementInFocus;
      setTimeout(() => {
         this.searchInput?.focus();
      }, 500);
   };

   private _handleChangeEvent = () => {
      const value = this.searchInput?.value ?? '';

      this.toggled = value?.length > 0;
      if (value?.length >= 4) {
         this.options = undefined;
         this.loading = true;

         this.dispatchEvent(new AtlExpandingSearchKeyDownEvent(value));
      }
   };

   private _throttledChangeEvent = throttle(this._handleChangeEvent, 500);

   protected _handleSelection = (value: string) => {
      const selectedValue =
         (this.options && this.options.find((o) => o.value === value)) ??
         undefined;
      this.toggled = false;
      this.dispatchEvent(new AtlExpandingSearchChangeEvent(selectedValue));
      this.handleClear();
   };

   protected _handleItemKeyDown = (e: KeyboardEvent, value: string) => {
      if (e.key === 'Enter' && this.options?.length) {
         this._handleSelection(value);
      }
   };

   protected handleClear = () => {
      this.elementInFocus = false;
      this.toggled = false;
      this.loading = false;

      if (this.searchInput) {
         this.searchInput.value = '';
      }
   };
}
